import React from 'react';
import { defineMessages, useIntl } from 'react-intl';
import { StyleSheet } from 'react-native';

import { AnimatedViews } from '@sg/garnish';

import { ErrorFallback } from '../../../../components';
import { type useIdMeVerification } from '../../../../integrations/idme';
import { type useIdMeVerificationContent } from '../../hooks';
import { IdMeVerificationLoading } from '../IdMeVerificationLoading';
import { IdMeVerificationPrompt } from '../IdMeVerificationPrompt';

// ─────────────────────────────────────────────────────────────────────────────

export const IdMeVerificationContent = (props: Props) => {
  const { verification, content } = props;

  const { formatMessage } = useIntl();

  // ─── Derived ───────────────────────────────────────────────────────────────

  const isPreparing = verification.isLoading || content.isLoading;
  const revealKey = verification.isVerified ? 'verified' : 'verify';

  // ───────────────────────────────────────────────────────────────────────────

  if (verification.hasLoadFailed) {
    return (
      <ErrorFallback
        description={formatMessage(messages.loadFailedDescription)}
        ctaLabel={formatMessage(messages.tryAgain)}
        onCtaPress={verification.retry}
      />
    );
  }

  if (isPreparing) {
    return <IdMeVerificationLoading />;
  }

  return (
    <AnimatedViews.Reveal key={revealKey} style={styles.reveal}>
      <IdMeVerificationPrompt verification={verification} content={content} />
    </AnimatedViews.Reveal>
  );
};

// ─── Messages ────────────────────────────────────────────────────────────────

const messages = defineMessages({
  loadFailedDescription: {
    defaultMessage: 'We could not check your ID.me verification. Please try again.',
    description: 'ID.me | Verification status failed to load | Description',
  },
  tryAgain: {
    defaultMessage: 'Try again',
    description: 'ID.me | Verification status failed to load | Retry button',
  },
});

// ─── Styles ──────────────────────────────────────────────────────────────────

const styles = StyleSheet.create({
  reveal: {
    flexGrow: 1,
  },
});

// ─── Types ───────────────────────────────────────────────────────────────────

type Props = {
  verification: ReturnType<typeof useIdMeVerification>;
  content: ReturnType<typeof useIdMeVerificationContent>;
};
